import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Save,
  Eye,
  Plus,
  FileText,
  Video,
  Image,
  Music,
  Link,
  Package,
  Trash2,
  Edit,
  Upload,
  CheckCircle,
  Circle,
  Target,
  Calendar,
  Clock,
  Users,
  BookOpen,
  Lightbulb,
  Trophy,
  Star,
  Award,
  Send,
  Settings,
  GripVertical,
  Volume2,
  FileType,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { useToast } from '@/hooks/use-toast';
import { FileUploadArea } from '@/components/course/FileUploadArea';
import { RichTextEditor, type RichTextEditorRef } from '@/components/course/RichTextEditor';
import { SubmitCourseProposalDialog } from '@/components/course/SubmitCourseProposalDialog';
import { CourseProposalForm } from '@/components/course/CourseProposalForm';
import { VideoConferencingSettings } from '@/components/course/VideoConferencingSettings';
import { WebinarScheduler } from '@/components/course/WebinarScheduler';
import { InteractiveElementSelector } from '@/components/course/InteractiveElementSelector';

interface Course {
  id: string;
  title: string;
  description: string;
  status: string;
  delivery_method?: string;
  is_online?: boolean;
  meeting_platform?: string;
  meeting_url?: string;
  meeting_id?: string;
  meeting_passcode?: string;
  meeting_instructions?: string;
  executive_summary?: string;
  target_educator_description?: string;
  learning_objectives?: string[];
  learning_outcomes?: string[];
}

interface CourseModule {
  id: string;
  course_id: string;
  title: string;
  description?: string;
  module_order: number;
  duration_minutes?: number;
  is_required: boolean;
}

interface CourseContent {
  id: string;
  module_id: string;
  title: string;
  content_type: string;
  content_url?: string;
  content_text?: string;
  youtube_url?: string;
  vimeo_url?: string;
  duration_minutes?: number;
  content_order: number;
  metadata?: any;
}

interface DeliverySettings {
  delivery_method: string;
  allows_async_completion: boolean;
  requires_live_attendance: boolean;
  self_paced: boolean;
  completion_criteria: any;
  settings: any;
}

interface SortableModuleItemProps {
  module: CourseModule;
  isSelected: boolean;
  onSelect: (module: CourseModule) => void;
  onEdit: (module: CourseModule) => void;
}

// Sortable content item component
interface SortableContentItemProps {
  content: any;
  onEdit: (content: any) => void;
  onDelete: (id: string) => void;
}

const SortableContentItem = ({ content, onEdit, onDelete }: SortableContentItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: content.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case 'rich_text':
        return <FileText className="h-5 w-5" />;
      case 'video':
        return <Video className="h-5 w-5" />;
      case 'audio':
        return <Volume2 className="h-5 w-5" />;
      case 'image':
        return <Image className="h-5 w-5" />;
      case 'document':
        return <FileType className="h-5 w-5" />;
      case 'url':
        return <ExternalLink className="h-5 w-5" />;
      case 'scorm':
        return <Package className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="flex items-center justify-between p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center space-x-3">
        <div 
          {...attributes} 
          {...listeners} 
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        {getContentIcon(content.content_type)}
        <div>
          <div className="font-medium">{content.title}</div>
          <div className="text-sm text-muted-foreground capitalize">
            {content.content_type.replace('_', ' ')}
          </div>
        </div>
      </div>
      <div className="flex space-x-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(content)}>
          <Edit className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(content.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default function CourseBuilderEnhanced() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isInstructor } = useRole();
  const { toast } = useToast();

  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null);
  const [moduleContent, setModuleContent] = useState<CourseContent[]>([]);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>({
    delivery_method: 'online',
    allows_async_completion: true,
    requires_live_attendance: false,
    self_paced: true,
    completion_criteria: {},
    settings: {}
  });
  const [loading, setLoading] = useState(true);
  const [showAddModuleDialog, setShowAddModuleDialog] = useState(false);
  const [showEditModuleDialog, setShowEditModuleDialog] = useState(false);
  const [showAddContentDialog, setShowAddContentDialog] = useState(false);
  const [showEditContentDialog, setShowEditContentDialog] = useState(false);
  const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
  const [editingContent, setEditingContent] = useState<CourseContent | null>(null);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [showInteractiveSelector, setShowInteractiveSelector] = useState(false);
  const [newModuleDescription, setNewModuleDescription] = useState('');
  const [newContentTitle, setNewContentTitle] = useState('');
  const [newContentType, setNewContentType] = useState('rich_text');
  const [newContentData, setNewContentData] = useState<any>({});
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const contentSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const courseBuilderSteps = [
    {
      id: 'setup',
      title: 'Course Setup & Information',
      description: 'Define your course structure and basic information',
      tasks: [
        'Set course title and description',
        'Choose delivery method',
        'Define target audience',
        'Set completion criteria'
      ]
    },
    {
      id: 'modules',
      title: 'Create Course Modules',
      description: 'Organize your content into logical learning modules',
      tasks: [
        'Plan module sequence',
        'Create module titles and descriptions',
        'Set module learning objectives',
        'Define module prerequisites'
      ]
    },
    {
      id: 'content',
      title: 'Develop Learning Content',
      description: 'Add engaging content to each module',
      tasks: [
        'Upload video content',
        'Create text-based lessons',
        'Add interactive elements',
        'Include assessments'
      ]
    },
    {
      id: 'engagement',
      title: 'Enhance Engagement',
      description: 'Add interactive features and assessments',
      tasks: [
        'Create knowledge checks',
        'Add discussion prompts',
        'Include multimedia elements',
        'Design practice activities'
      ]
    },
    {
      id: 'proposal',
      title: 'Submit for Approval',
      description: 'Submit your completed course for administrative review',
      tasks: [
        'Review course completeness',
        'Add justification and objectives',
        'Set target audience and budget',
        'Submit proposal to administrators'
      ]
    }
  ];

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
      fetchModules();
      fetchDeliverySettings();
    }
  }, [courseId]);

  useEffect(() => {
    if (selectedModule) {
      fetchModuleContent(selectedModule.id);
    }
  }, [selectedModule]);

  useEffect(() => {
    // Calculate progress based on completed steps and course content
    const totalSteps = courseBuilderSteps.length;
    const modulesCreated = modules.length > 0 ? 1 : 0;
    const contentCreated = moduleContent.length > 0 ? 1 : 0;
    const settingsConfigured = deliverySettings.delivery_method !== 'in_person' ? 1 : 0;
    
    const baseProgress = (completedSteps.length / totalSteps) * 60; // 60% for step completion
    const contentProgress = (modulesCreated + contentCreated + settingsConfigured) * 13.33; // 40% for actual content
    
    setProgress(Math.min(baseProgress + contentProgress, 100));
  }, [completedSteps, modules, moduleContent, deliverySettings]);

  const markStepComplete = (stepId: string) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const fetchCourseData = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (error) throw error;
      setCourse(data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast({
        title: "Error",
        description: "Failed to fetch course data",
        variant: "destructive"
      });
    }
  };

  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('course_modules')
        .select('*')
        .eq('course_id', courseId)
        .order('module_order');

      if (error) throw error;
      setModules(data || []);
      if (data && data.length > 0 && !selectedModule) {
        setSelectedModule(data[0]);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleContent = async (moduleId: string) => {
    try {
      const { data, error } = await supabase
        .from('course_content')
        .select('*')
        .eq('module_id', moduleId)
        .order('content_order');

      if (error) throw error;
      setModuleContent(data || []);
    } catch (error) {
      console.error('Error fetching module content:', error);
    }
  };

  const fetchDeliverySettings = async () => {
    try {
      const { data, error } = await supabase
        .from('course_delivery_settings')
        .select('*')
        .eq('course_id', courseId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) {
        setDeliverySettings(data);
      }
    } catch (error) {
      console.error('Error fetching delivery settings:', error);
    }
  };

  const handleContentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = moduleContent.findIndex((content) => content.id === active.id);
      const newIndex = moduleContent.findIndex((content) => content.id === over?.id);

      const newContent = arrayMove(moduleContent, oldIndex, newIndex);
      setModuleContent(newContent);

      // Update content_order in database
      updateContentOrder(newContent);
    }
  };

  const updateContentOrder = async (reorderedContent: any[]) => {
    try {
      const updates = reorderedContent.map((content, index) => ({
        id: content.id,
        content_order: index + 1,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('course_content')
          .update({ content_order: update.content_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Content order updated successfully"
      });
    } catch (error) {
      console.error('Error updating content order:', error);
      toast({
        title: "Error",
        description: "Failed to update content order",
        variant: "destructive"
      });
    }
  };

  const createModule = async () => {
    if (!newModuleTitle.trim()) return;

    try {
      const maxOrder = Math.max(...modules.map(m => m.module_order), 0);
      const { data, error } = await supabase
        .from('course_modules')
        .insert({
          course_id: courseId!,
          title: newModuleTitle,
          description: newModuleDescription || null,
          module_order: maxOrder + 1,
          is_required: true
        })
        .select()
        .single();

      if (error) throw error;

      setModules([...modules, data]);
      setNewModuleTitle('');
      setNewModuleDescription('');
      setShowAddModuleDialog(false);
      toast({
        title: "Success",
        description: "Module created successfully"
      });
    } catch (error) {
      console.error('Error creating module:', error);
      toast({
        title: "Error",
        description: "Failed to create module",
        variant: "destructive"
      });
    }
  };

  const openEditModuleDialog = (module: CourseModule) => {
    setEditingModule(module);
    setNewModuleTitle(module.title);
    setNewModuleDescription(module.description || '');
    setShowEditModuleDialog(true);
  };

  const updateModule = async () => {
    if (!newModuleTitle.trim() || !editingModule) return;

    try {
      const { data, error } = await supabase
        .from('course_modules')
        .update({
          title: newModuleTitle,
          description: newModuleDescription || null,
        })
        .eq('id', editingModule.id)
        .select()
        .single();

      if (error) throw error;

      setModules(modules.map(m => m.id === editingModule.id ? data : m));
      if (selectedModule?.id === editingModule.id) {
        setSelectedModule(data);
      }
      
      setNewModuleTitle('');
      setNewModuleDescription('');
      setEditingModule(null);
      setShowEditModuleDialog(false);
      toast({
        title: "Success",
        description: "Module updated successfully"
      });
    } catch (error) {
      console.error('Error updating module:', error);
      toast({
        title: "Error",
        description: "Failed to update module",
        variant: "destructive"
      });
    }
  };

  const createContent = async () => {
    if (!newContentTitle.trim() || !selectedModule) return;

    try {
      const maxOrder = Math.max(...moduleContent.map(c => c.content_order), 0);
      
      // Get content from rich text editor if using rich_text type
      let contentData: any = {
        module_id: selectedModule.id,
        title: newContentTitle,
        content_type: newContentType,
        content_order: maxOrder + 1,
        ...newContentData
      };

      // For rich text content, get the content from the editor
      if (newContentType === 'rich_text' && richTextEditorRef.current) {
        contentData.content_text = richTextEditorRef.current.getContent();
      }

      const { data, error } = await supabase
        .from('course_content')
        .insert(contentData)
        .select()
        .single();

      if (error) throw error;

      setModuleContent([...moduleContent, data]);
      setNewContentTitle('');
      setNewContentType('rich_text');
      setNewContentData({});
      setShowAddContentDialog(false);
      
      // Clear the rich text editor
      if (richTextEditorRef.current) {
        richTextEditorRef.current.setContent('');
      }
      
      toast({
        title: "Success",
        description: "Content added successfully"
      });
    } catch (error) {
      console.error('Error creating content:', error);
      toast({
        title: "Error",
        description: "Failed to add content",
        variant: "destructive"
      });
    }
  };

  const openEditContentDialog = (content: CourseContent) => {
    setEditingContent(content);
    setNewContentTitle(content.title);
    setNewContentType(content.content_type);
    setNewContentData({
      content_url: content.content_url,
      content_text: content.content_text,
      youtube_url: content.youtube_url,
      vimeo_url: content.vimeo_url,
      metadata: content.metadata
    });
    
    // Set rich text editor content if it's rich text type
    if (content.content_type === 'rich_text' && content.content_text && richTextEditorRef.current) {
      richTextEditorRef.current.setContent(content.content_text);
    }
    
    setShowEditContentDialog(true);
  };

  const updateContent = async () => {
    if (!newContentTitle.trim() || !editingContent) return;

    try {
      let contentData: any = {
        title: newContentTitle,
        content_type: newContentType,
        ...newContentData
      };

      // For rich text content, get the content from the editor
      if (newContentType === 'rich_text' && richTextEditorRef.current) {
        contentData.content_text = richTextEditorRef.current.getContent();
      }

      const { data, error } = await supabase
        .from('course_content')
        .update(contentData)
        .eq('id', editingContent.id)
        .select()
        .single();

      if (error) throw error;

      setModuleContent(moduleContent.map(c => c.id === editingContent.id ? data : c));
      setNewContentTitle('');
      setNewContentType('rich_text');
      setNewContentData({});
      setEditingContent(null);
      setShowEditContentDialog(false);
      
      // Clear the rich text editor
      if (richTextEditorRef.current) {
        richTextEditorRef.current.setContent('');
      }
      
      toast({
        title: "Success",
        description: "Content updated successfully"
      });
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "Error",
        description: "Failed to update content",
        variant: "destructive"
      });
    }
  };

  const deleteContent = async (contentId: string) => {
    try {
      const { error } = await supabase
        .from('course_content')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      setModuleContent(moduleContent.filter(c => c.id !== contentId));
      toast({
        title: "Success",
        description: "Content deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: "Error",
        description: "Failed to delete content",
        variant: "destructive"
      });
    }
  };

  const saveDeliverySettings = async () => {
    try {
      const { error } = await supabase
        .from('course_delivery_settings')
        .upsert({
          course_id: courseId!,
          ...deliverySettings
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Delivery settings saved"
      });
    } catch (error) {
      console.error('Error saving delivery settings:', error);
      toast({
        title: "Error",
        description: "Failed to save delivery settings",
        variant: "destructive"
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = modules.findIndex((module) => module.id === active.id);
      const newIndex = modules.findIndex((module) => module.id === over?.id);

      const reorderedModules = arrayMove(modules, oldIndex, newIndex);
      setModules(reorderedModules);

      // Update module orders in the database
      try {
        const updates = reorderedModules.map((module, index) => ({
          id: module.id,
          module_order: index + 1
        }));

        for (const update of updates) {
          await supabase
            .from('course_modules')
            .update({ module_order: update.module_order })
            .eq('id', update.id);
        }

        toast({
          title: "Success",
          description: "Module order updated successfully"
        });
      } catch (error) {
        console.error('Error updating module order:', error);
        toast({
          title: "Error",
          description: "Failed to update module order",
          variant: "destructive"
        });
        // Revert the order if database update fails
        fetchModules();
      }
    }
  };

  const getContentIcon = (contentType: string) => {
    switch (contentType) {
      case 'rich_text': return <FileText className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      case 'image': return <Image className="h-4 w-4" />;
      case 'presentation': return <FileText className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      case 'url': return <Link className="h-4 w-4" />;
      case 'scorm': return <Package className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const SortableModuleItem: React.FC<SortableModuleItemProps> = ({ module, isSelected, onSelect, onEdit }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: module.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`p-3 rounded-lg border transition-colors flex items-center gap-3 ${
          isSelected 
            ? 'bg-primary text-primary-foreground border-primary' 
            : 'bg-muted hover:bg-muted/80 border-transparent'
        } ${isDragging ? 'opacity-50' : ''}`}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => onSelect(module)}
        >
          <div className="font-medium text-sm truncate">{module.title}</div>
          {module.description && (
            <div className="text-xs opacity-75 mt-1 truncate">{module.description}</div>
          )}
        </div>
        <Button
          size="sm"
          variant={isSelected ? "secondary" : "outline"}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(module);
          }}
          className="flex-shrink-0"
        >
          <Edit className="h-3 w-3" />
        </Button>
      </div>
    );
  };

  if (loading) {
    return <div className="p-6">Loading course builder...</div>;
  }

  if (!course) {
    return <div className="p-6">Course not found</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3">
          <BookOpen className="h-12 w-12 text-primary" />
          <h1 className="text-4xl font-bold">Course Builder</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Create comprehensive learning experiences with guided course development
        </p>
      </div>

      {/* Course Title and Description Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Course Information</CardTitle>
          <CardDescription>Edit your course title and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="course-title">Course Title</Label>
            <Input
              id="course-title"
              value={course.title}
              onChange={(e) => setCourse({ ...course, title: e.target.value })}
              onBlur={async () => {
                try {
                  const { error } = await supabase
                    .from('courses')
                    .update({ title: course.title })
                    .eq('id', courseId);
                  if (error) throw error;
                  toast({
                    title: "Success",
                    description: "Course title updated"
                  });
                } catch (error) {
                  console.error('Error updating title:', error);
                  toast({
                    title: "Error",
                    description: "Failed to update course title",
                    variant: "destructive"
                  });
                }
              }}
              placeholder="Enter course title..."
              className="text-lg font-semibold"
            />
          </div>
          <div>
            <Label htmlFor="course-description">Course Description</Label>
            <Textarea
              id="course-description"
              value={course.description || ''}
              onChange={(e) => setCourse({ ...course, description: e.target.value })}
              onBlur={async () => {
                try {
                  const { error } = await supabase
                    .from('courses')
                    .update({ description: course.description })
                    .eq('id', courseId);
                  if (error) throw error;
                  toast({
                    title: "Success",
                    description: "Course description updated"
                  });
                } catch (error) {
                  console.error('Error updating description:', error);
                  toast({
                    title: "Error",
                    description: "Failed to update course description",
                    variant: "destructive"
                  });
                }
              }}
              placeholder="Enter course description..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="target-educator">Ideal Educator Profile</Label>
            <Input
              id="target-educator"
              value={course.target_educator_description || ''}
              onChange={(e) => setCourse({ ...course, target_educator_description: e.target.value })}
              onBlur={async () => {
                try {
                  const { error } = await supabase
                    .from('courses')
                    .update({ target_educator_description: course.target_educator_description })
                    .eq('id', courseId);
                  if (error) throw error;
                  toast({
                    title: "Success",
                    description: "Target educator description updated"
                  });
                } catch (error) {
                  console.error('Error updating target educator:', error);
                  toast({
                    title: "Error",
                    description: "Failed to update target educator description",
                    variant: "destructive"
                  });
                }
              }}
              placeholder="This is great for an educator who..."
            />
          </div>

          <div>
            <Label htmlFor="executive-summary">Executive Summary</Label>
            <Textarea
              id="executive-summary"
              value={course.executive_summary || ''}
              onChange={(e) => setCourse({ ...course, executive_summary: e.target.value })}
              onBlur={async () => {
                try {
                  const { error } = await supabase
                    .from('courses')
                    .update({ executive_summary: course.executive_summary })
                    .eq('id', courseId);
                  if (error) throw error;
                  toast({
                    title: "Success",
                    description: "Executive summary updated"
                  });
                } catch (error) {
                  console.error('Error updating executive summary:', error);
                  toast({
                    title: "Error",
                    description: "Failed to update executive summary",
                    variant: "destructive"
                  });
                }
              }}
              placeholder="Provide a detailed overview of the course..."
              rows={5}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Learning Objectives</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const objectives = course.learning_objectives || [];
                  setCourse({ ...course, learning_objectives: [...objectives, ''] });
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Objective
              </Button>
            </div>
            <div className="space-y-2">
              {(course.learning_objectives || []).map((objective, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={objective}
                    onChange={(e) => {
                      const objectives = [...(course.learning_objectives || [])];
                      objectives[index] = e.target.value;
                      setCourse({ ...course, learning_objectives: objectives });
                    }}
                    onBlur={async () => {
                      try {
                        const { error } = await supabase
                          .from('courses')
                          .update({ learning_objectives: course.learning_objectives })
                          .eq('id', courseId);
                        if (error) throw error;
                        toast({
                          title: "Success",
                          description: "Learning objectives updated"
                        });
                      } catch (error) {
                        console.error('Error updating objectives:', error);
                        toast({
                          title: "Error",
                          description: "Failed to update learning objectives",
                          variant: "destructive"
                        });
                      }
                    }}
                    placeholder={`Objective ${index + 1}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const objectives = (course.learning_objectives || []).filter((_, i) => i !== index);
                      setCourse({ ...course, learning_objectives: objectives });
                      supabase
                        .from('courses')
                        .update({ learning_objectives: objectives })
                        .eq('id', courseId)
                        .then(() => {
                          toast({
                            title: "Success",
                            description: "Learning objective removed"
                          });
                        });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(!course.learning_objectives || course.learning_objectives.length === 0) && (
                <p className="text-sm text-muted-foreground">No learning objectives yet. Click "Add Objective" to create one.</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Learning Outcomes</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const outcomes = course.learning_outcomes || [];
                  setCourse({ ...course, learning_outcomes: [...outcomes, ''] });
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Outcome
              </Button>
            </div>
            <div className="space-y-2">
              {(course.learning_outcomes || []).map((outcome, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={outcome}
                    onChange={(e) => {
                      const outcomes = [...(course.learning_outcomes || [])];
                      outcomes[index] = e.target.value;
                      setCourse({ ...course, learning_outcomes: outcomes });
                    }}
                    onBlur={async () => {
                      try {
                        const { error } = await supabase
                          .from('courses')
                          .update({ learning_outcomes: course.learning_outcomes })
                          .eq('id', courseId);
                        if (error) throw error;
                        toast({
                          title: "Success",
                          description: "Learning outcomes updated"
                        });
                      } catch (error) {
                        console.error('Error updating outcomes:', error);
                        toast({
                          title: "Error",
                          description: "Failed to update learning outcomes",
                          variant: "destructive"
                        });
                      }
                    }}
                    placeholder={`Outcome ${index + 1}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const outcomes = (course.learning_outcomes || []).filter((_, i) => i !== index);
                      setCourse({ ...course, learning_outcomes: outcomes });
                      supabase
                        .from('courses')
                        .update({ learning_outcomes: outcomes })
                        .eq('id', courseId)
                        .then(() => {
                          toast({
                            title: "Success",
                            description: "Learning outcome removed"
                          });
                        });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(!course.learning_outcomes || course.learning_outcomes.length === 0) && (
                <p className="text-sm text-muted-foreground">No learning outcomes yet. Click "Add Outcome" to create one.</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="secondary" className="text-sm">
              <Award className="h-4 w-4 mr-1" />
              Status: {course.status}
            </Badge>
            <Badge variant="outline" className="text-sm">
              <Clock className="h-4 w-4 mr-1" />
              Professional Course Creation
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Your Course Development Progress</CardTitle>
          <CardDescription>Track your journey toward course completion</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <div className="text-sm font-medium">Modules</div>
                <Progress 
                  value={modules.length > 0 ? 100 : 0} 
                  className="h-2 mt-1" 
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {modules.length} created
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium">Content</div>
                <Progress 
                  value={moduleContent.length > 0 ? 100 : 0} 
                  className="h-2 mt-1" 
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {moduleContent.length} items
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium">Settings</div>
                <Progress 
                  value={deliverySettings.delivery_method !== 'in_person' ? 100 : 0} 
                  className="h-2 mt-1" 
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {deliverySettings.delivery_method !== 'in_person' ? 'Configured' : 'Pending'}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate('/my-courses')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Courses
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.open(`/course-player/${courseId}`, '_blank')}
          >
            <Eye className="h-4 w-4 mr-2" />
            Preview Course
          </Button>
          <Button onClick={() => navigate(`/course-management/${courseId}`)}>
            <Save className="h-4 w-4 mr-2" />
            Save & Exit
          </Button>
        </div>
      </div>

      <Tabs defaultValue="guided-builder" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="guided-builder">Guided Steps</TabsTrigger>
          <TabsTrigger value="modules">Modules & Content</TabsTrigger>
          <TabsTrigger value="live-webinar">Live Webinar</TabsTrigger>
          <TabsTrigger value="settings">Course Settings</TabsTrigger>
          <TabsTrigger value="preview">Preview & Publish</TabsTrigger>
          <TabsTrigger value="proposal">Submit Proposal</TabsTrigger>
        </TabsList>

        <TabsContent value="guided-builder" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Course Development Steps</h2>
            <p className="text-muted-foreground">
              Follow these guided steps to create a comprehensive learning experience
            </p>
          </div>
          
          <div className="grid gap-6">
            {courseBuilderSteps.map((step, index) => (
              <Card key={step.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-lg bg-primary text-primary-foreground">
                      <Target className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        Step {index + 1}: {step.title}
                      </CardTitle>
                      <CardDescription>{step.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-3">
                      {step.tasks.map((task, taskIndex) => (
                        <div key={taskIndex} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
                          <Circle className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span className="text-sm">{task}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      onClick={() => markStepComplete(step.id)}
                      disabled={completedSteps.includes(step.id)}
                      className="w-full"
                    >
                      {completedSteps.includes(step.id) ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Completed
                        </>
                      ) : (
                        'Mark Complete'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="modules" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Course Modules & Content</h2>
            <p className="text-muted-foreground">
              Organize your learning materials into structured modules
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Modules List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Course Modules</CardTitle>
                  <Dialog open={showAddModuleDialog} onOpenChange={setShowAddModuleDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Module
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Module</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="module-title">Module Title</Label>
                          <Input
                            id="module-title"
                            value={newModuleTitle}
                            onChange={(e) => setNewModuleTitle(e.target.value)}
                            placeholder="Enter module title..."
                          />
                        </div>
                        <div>
                          <Label htmlFor="module-description">Description (Optional)</Label>
                          <Textarea
                            id="module-description"
                            value={newModuleDescription}
                            onChange={(e) => setNewModuleDescription(e.target.value)}
                            placeholder="Brief description of the module..."
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" onClick={() => setShowAddModuleDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={createModule}>
                            Create Module
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Edit Module Dialog */}
                  <Dialog open={showEditModuleDialog} onOpenChange={setShowEditModuleDialog}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit Module</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="edit-module-title">Module Title</Label>
                          <Input
                            id="edit-module-title"
                            value={newModuleTitle}
                            onChange={(e) => setNewModuleTitle(e.target.value)}
                            placeholder="Enter module title..."
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-module-description">Description (Optional)</Label>
                          <Textarea
                            id="edit-module-description"
                            value={newModuleDescription}
                            onChange={(e) => setNewModuleDescription(e.target.value)}
                            placeholder="Brief description of the module..."
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" onClick={() => setShowEditModuleDialog(false)}>
                            Cancel
                          </Button>
                          <Button onClick={updateModule}>
                            Update Module
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {modules.map((module) => (
                    <div
                      key={module.id}
                      className={`p-3 rounded-lg border transition-colors ${
                        selectedModule?.id === module.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted hover:bg-muted/80 border-transparent'
                      }`}
                    >
                      <div 
                        className="cursor-pointer"
                        onClick={() => setSelectedModule(module)}
                      >
                        <div className="font-medium text-sm">{module.title}</div>
                        {module.description && (
                          <div className="text-xs opacity-75 mt-1">{module.description}</div>
                        )}
                      </div>
                      <div className="flex justify-end mt-2">
                        <Button
                          size="sm"
                          variant={selectedModule?.id === module.id ? "secondary" : "outline"}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModuleDialog(module);
                          }}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  {modules.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No modules created yet. Click "Add Module" to get started.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Module Content */}
            <div className="lg:col-span-2">
              {selectedModule ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{selectedModule.title}</CardTitle>
                        <CardDescription>Manage content for this module</CardDescription>
                      </div>
                      <Dialog open={showAddContentDialog} onOpenChange={setShowAddContentDialog}>
                        <DialogTrigger asChild>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Content
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Add New Content</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="content-title">Content Title</Label>
                              <Input
                                id="content-title"
                                value={newContentTitle}
                                onChange={(e) => setNewContentTitle(e.target.value)}
                                placeholder="Enter content title..."
                              />
                            </div>
                            <div>
                              <Label htmlFor="content-type">Content Type</Label>
                              <Select 
                                value={newContentType} 
                                onValueChange={(value) => {
                                  setNewContentType(value);
                                  if (value === 'interactive') {
                                    setShowInteractiveSelector(true);
                                  }
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="rich_text">Rich Text / HTML Content</SelectItem>
                                  <SelectItem value="video">Video</SelectItem>
                                  <SelectItem value="audio">Audio</SelectItem>
                                  <SelectItem value="image">Image</SelectItem>
                                  <SelectItem value="document">Document</SelectItem>
                                  <SelectItem value="url">External Link</SelectItem>
                                  <SelectItem value="scorm">SCORM Package</SelectItem>
                                  <SelectItem value="interactive">Interactive Element</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Rich Text Editor for rich_text type */}
                            {newContentType === 'rich_text' && (
                              <div>
                                <Label>Content</Label>
                                <RichTextEditor
                                  ref={richTextEditorRef}
                                  value={editingContent?.content_text || ''}
                                  onChange={(content) => setNewContentData({ ...newContentData, content_text: content })}
                                  placeholder="Edit your lesson content with rich formatting, images, and videos..."
                                />
                              </div>
                            )}

                            {/* Interactive Element Selector */}
                            {newContentType === 'interactive' && (
                              <InteractiveElementSelector
                                open={showInteractiveSelector}
                                onClose={() => setShowInteractiveSelector(false)}
                                onSelect={(element) => {
                                  setNewContentData({
                                    content_text: JSON.stringify(element.config),
                                    metadata: { interactiveType: element.type, ...element.config }
                                  });
                                  setNewContentTitle(element.title);
                                  setShowInteractiveSelector(false);
                                }}
                              />
                            )}

                            {/* File upload for other content types */}
                            {newContentType !== 'rich_text' && newContentType !== 'url' && newContentType !== 'interactive' && (
                              <FileUploadArea
                                onFilesSelected={(files) => {
                                  if (files.length > 0) {
                                    setNewContentData({
                                      content_url: files[0].name,
                                      metadata: { filename: files[0].name, type: files[0].type }
                                    });
                                  }
                                }}
                                acceptedFileTypes={
                                  newContentType === 'video' ? 'video/*' :
                                  newContentType === 'audio' ? 'audio/*' :
                                  newContentType === 'image' ? 'image/*' :
                                  '*'
                                }
                                multiple={false}
                              />
                            )}

                            {/* URL input for external links */}
                            {newContentType === 'url' && (
                              <div>
                                <Label htmlFor="edit-content-url">URL</Label>
                                <Input
                                  id="edit-content-url"
                                  type="url"
                                  placeholder="https://..."
                                  value={newContentData.content_url || ''}
                                  onChange={(e) => setNewContentData({
                                    ...newContentData,
                                    content_url: e.target.value
                                  })}
                                />
                              </div>
                            )}

                            <div className="flex justify-end space-x-2">
                              <Button variant="outline" onClick={() => setShowAddContentDialog(false)}>
                                Cancel
                              </Button>
                              <Button onClick={createContent}>
                                Add Content
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <DndContext
                        sensors={contentSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleContentDragEnd}
                      >
                        <SortableContext
                          items={moduleContent.map(content => content.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {moduleContent.map((content) => (
                            <SortableContentItem 
                              key={content.id} 
                              content={content} 
                              onEdit={openEditContentDialog}
                              onDelete={deleteContent}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                      
                       {moduleContent.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          No content added yet. Click "Add Content" to get started.
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <div className="text-center text-muted-foreground">
                      Select a module to view and edit its content
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Edit Content Dialog */}
          <Dialog open={showEditContentDialog} onOpenChange={setShowEditContentDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Content</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-content-title">Content Title</Label>
                  <Input
                    id="edit-content-title"
                    value={newContentTitle}
                    onChange={(e) => setNewContentTitle(e.target.value)}
                    placeholder="Enter content title..."
                  />
                </div>
                <div>
                  <Label htmlFor="edit-content-type">Content Type</Label>
                  <Select 
                    value={newContentType} 
                    onValueChange={(value) => setNewContentType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rich_text">Rich Text / HTML Content</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="url">External Link</SelectItem>
                      <SelectItem value="scorm">SCORM Package</SelectItem>
                      <SelectItem value="interactive">Interactive Element</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Rich Text Editor for rich_text type */}
                {newContentType === 'rich_text' && (
                  <div>
                    <Label>Content</Label>
                    <RichTextEditor
                      ref={richTextEditorRef}
                      onChange={(content) => setNewContentData({ ...newContentData, content_text: content })}
                      placeholder="Edit your lesson content with rich formatting, images, and videos..."
                    />
                  </div>
                )}

                {/* Interactive Element for interactive type */}
                {newContentType === 'interactive' && (
                  <InteractiveElementSelector
                    open={showInteractiveSelector}
                    onClose={() => setShowInteractiveSelector(false)}
                    onSelect={(element) => {
                      setNewContentData({
                        content_text: JSON.stringify(element.config),
                        metadata: { interactiveType: element.type, ...element.config }
                      });
                      setNewContentTitle(element.title);
                      setShowInteractiveSelector(false);
                    }}
                  />
                )}

                {/* File upload for other content types */}
                {newContentType !== 'rich_text' && newContentType !== 'url' && newContentType !== 'interactive' && (
                  <FileUploadArea
                    onFilesSelected={(files) => {
                      if (files.length > 0) {
                        setNewContentData({
                          content_url: files[0].name,
                          metadata: { filename: files[0].name, type: files[0].type }
                        });
                      }
                    }}
                    acceptedFileTypes={
                      newContentType === 'video' ? 'video/*' :
                      newContentType === 'audio' ? 'audio/*' :
                      newContentType === 'image' ? 'image/*' :
                      '*'
                    }
                    multiple={false}
                  />
                )}

                {/* URL input for external links */}
                {newContentType === 'url' && (
                  <div>
                    <Label htmlFor="edit-url">URL</Label>
                    <Input
                      id="edit-url"
                      type="url"
                      placeholder="https://..."
                      value={newContentData.content_url || ''}
                      onChange={(e) => setNewContentData({
                        ...newContentData,
                        content_url: e.target.value
                      })}
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => {
                    setShowEditContentDialog(false);
                    setEditingContent(null);
                    setNewContentTitle('');
                    setNewContentType('rich_text');
                    setNewContentData({});
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={updateContent}>
                    Update Content
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Course Settings</h2>
            <p className="text-muted-foreground">
              Configure how your course will be delivered and accessed
            </p>
          </div>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-lg bg-blue-500 text-white">
                  <Target className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-lg">Delivery Settings</CardTitle>
                  <CardDescription>Configure how your course will be delivered to learners</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="delivery-method">Delivery Method</Label>
                <Select 
                  value={deliverySettings.delivery_method} 
                  onValueChange={(value) => setDeliverySettings({ ...deliverySettings, delivery_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Fully Online</SelectItem>
                    <SelectItem value="blended">Blended Learning</SelectItem>
                    <SelectItem value="in_person">In-Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="self-paced">Self-Paced Learning</Label>
                  <p className="text-sm text-muted-foreground">Allow students to progress at their own speed</p>
                </div>
                <Switch
                  id="self-paced"
                  checked={deliverySettings.self_paced}
                  onCheckedChange={(checked) => setDeliverySettings({ ...deliverySettings, self_paced: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="async-completion">Asynchronous Completion</Label>
                  <p className="text-sm text-muted-foreground">Students can complete activities at any time</p>
                </div>
                <Switch
                  id="async-completion"
                  checked={deliverySettings.allows_async_completion}
                  onCheckedChange={(checked) => setDeliverySettings({ ...deliverySettings, allows_async_completion: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="live-attendance">Require Live Attendance</Label>
                  <p className="text-sm text-muted-foreground">Students must attend scheduled sessions</p>
                </div>
                <Switch
                  id="live-attendance"
                  checked={deliverySettings.requires_live_attendance}
                  onCheckedChange={(checked) => setDeliverySettings({ ...deliverySettings, requires_live_attendance: checked })}
                />
              </div>

              <Button onClick={saveDeliverySettings}>
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="live-webinar" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Live Webinar Scheduling</h2>
            <p className="text-muted-foreground">
              Schedule live webinar sessions for real-time interaction with participants
            </p>
          </div>

          <VideoConferencingSettings
            meetingPlatform={course?.meeting_platform || ''}
            meetingUrl={course?.meeting_url || ''}
            meetingId={course?.meeting_id || ''}
            meetingPasscode={course?.meeting_passcode || ''}
            meetingInstructions={course?.meeting_instructions || ''}
            onChange={async (field, value) => {
              const { error } = await supabase
                .from('courses')
                .update({ [field]: value })
                .eq('id', courseId);
              
              if (error) {
                toast({
                  title: "Error",
                  description: "Failed to update meeting settings",
                  variant: "destructive"
                });
              } else {
                setCourse({ ...course!, [field]: value });
                toast({
                  title: "Success",
                  description: "Meeting settings updated"
                });
              }
            }}
          />

          <WebinarScheduler
            courseId={courseId!}
            meetingPlatform={course?.meeting_platform}
            meetingUrl={course?.meeting_url}
            meetingId={course?.meeting_id}
            meetingPasscode={course?.meeting_passcode}
          />
        </TabsContent>

        <TabsContent value="preview" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Preview & Publish</h2>
            <p className="text-muted-foreground">
              Review your course and make it available to learners
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Course Summary */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-lg bg-primary text-primary-foreground">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Course Overview</CardTitle>
                    <CardDescription>Summary of your course structure</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-lg">{course.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{course.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 bg-primary rounded-full" />
                    <span><strong>Modules:</strong> {modules.length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 bg-green-500 rounded-full" />
                    <span><strong>Content:</strong> {moduleContent.length} items</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 bg-blue-500 rounded-full" />
                    <span><strong>Method:</strong> {deliverySettings.delivery_method}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="h-1.5 w-1.5 bg-purple-500 rounded-full" />
                    <span><strong>Self-Paced:</strong> {deliverySettings.self_paced ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Publishing Checklist */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-lg bg-green-500 text-white">
                    <Trophy className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Publishing Checklist</CardTitle>
                    <CardDescription>Complete these items before publishing</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${modules.length > 0 ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className="text-sm">At least one module created</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${moduleContent.length > 0 ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className="text-sm">Content added to modules</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${deliverySettings.delivery_method !== 'in_person' ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className="text-sm">Delivery settings configured</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className={`h-5 w-5 ${course.description ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className="text-sm">Course description added</span>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => window.open(`/course-player/${courseId}`, '_blank')}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview Course
                  </Button>
                  <Button 
                    className="w-full"
                    disabled={modules.length === 0 || moduleContent.length === 0}
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Publish Course
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="proposal" className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Submit Course Proposal</h2>
            <p className="text-muted-foreground">
              Submit your completed course for administrative review and approval
            </p>
          </div>

          {course && <CourseProposalForm course={{...course, is_online: course.is_online || false}} onSuccess={() => {
            toast({
              title: "Success",
              description: "Course proposal submitted successfully"
            });
            // Mark this step as completed
            if (!completedSteps.includes('proposal')) {
              setCompletedSteps([...completedSteps, 'proposal']);
            }
          }} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
